// LeaveManagementPage.jsx
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import LeaveFilters from './LeaveFilters';
import LeaveTable from './LeaveTable';
import LeaveStatCard from './LeaveStatCard';
import MiniCalendar from './MiniCalendar';
import EmptyState from './EmptyState';
import { PlusCircle, Check, CheckCircle, Zap } from 'lucide-react';
import axios from 'axios';

export default function LeaveManagementPage() {
  const [leaves, setLeaves] = useState([]);
  const [filteredLeaves, setFilteredLeaves] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [stats, setStats] = useState({ pending: 0, approved: 0 });
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Dummy leave data for UI preview
    const dummy = [
      {
        id: 1,
        name: 'Hashib Raja',
        leaveType: 'Vacation',
        startDate: '2025-07-28',
        endDate: '2025-07-30',
        status: 'approved',
      },
      {
        id: 2,
        name: 'Vishal Kumar',
        leaveType: 'Sick',
        startDate: '2025-07-25',
        endDate: '2025-07-26',
        status: 'pending',
      },
      {
        id: 3,
        name: 'Amit Kumar',
        leaveType: 'Parental',
        startDate: '2025-07-20',
        endDate: '2025-07-22',
        status: 'rejected',
      },
      {
        id: 4,
        name: 'Bhawna Singh',
        leaveType: 'Vacation',
        startDate: '2025-08-01',
        endDate: '2025-08-05',
        status: 'approved',
      },
      {
        id: 5,
        name: 'Eva Green',
        leaveType: 'Sick',
        startDate: '2025-07-27',
        endDate: '2025-07-27',
        status: 'pending',
      },
    ];
    setLeaves(dummy);
    setFilteredLeaves(dummy);
    setStats({
      pending: dummy.filter(l => l.status === 'pending').length,
      approved: dummy.filter(l => l.status === 'approved').length,
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    let result = leaves;
    if (filter !== 'all') {
      result = result.filter(leave => leave.status === filter);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(leave =>
        leave.name.toLowerCase().includes(query) ||
        leave.leaveType.toLowerCase().includes(query)
      );
    }
    setFilteredLeaves(result);
  }, [filter, searchQuery, leaves]);

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-700 rounded-2xl p-8 mb-8 shadow-xl flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Leave Management</h1>
          <p className="text-indigo-200">Track and manage time-off requests</p>
        </div>
        <div className="flex gap-3 justify-end">
          <button
            onClick={() => navigate('/leaves/new')}
            className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
          >
          <PlusCircle className="h-5 w-5" />
            Request Leave
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <LeaveStatCard title="Pending Requests" value={stats.pending} trend="+2 from last week" icon={Check} color="from-amber-500 to-amber-400" />
        <LeaveStatCard title="Approved This Month" value={stats.approved} trend="12% increase" icon={CheckCircle} color="from-emerald-500 to-emerald-400" />
        <LeaveStatCard title="Avg. Approval Time" value="1.8 days" trend="Faster than last month" icon={Zap} color="from-blue-500 to-indigo-400" />
      </div>

      {/* Main content and sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        {/* Main content: filters and table */}
        <div>
          <LeaveFilters filter={filter} setFilter={setFilter} searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
          <div className="mt-6">
            {filteredLeaves.length === 0 && !loading ? (
              <EmptyState onRequestLeave={() => navigate('/leaves/new')} />
            ) : (
              <LeaveTable leaves={filteredLeaves} />
            )}
          </div>
        </div>
        {/* Sidebar: mini calendar */}
        <div className="hidden lg:block">
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100 shadow-md">
            <MiniCalendar events={leaves.filter(l => l.status === 'approved')} />
          </div>
        </div>
      </div>
      {/* On mobile/tablet, show calendar below table */}
      <div className="block lg:hidden mt-8">
        <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-5 border border-indigo-100 shadow-md">
          <MiniCalendar events={leaves.filter(l => l.status === 'approved')} />
        </div>
      </div>
    </div>
  );
}
